<template>
  <div :class="cn()">
    <div :class="cn('gridWrap')">
      <div :class="cn('grid')">
        <HomeSectionProductsCard
          v-for="(product, k) in products"
          :key="product.id || `home_product_grid_${k}`"
          :product="product"
          :index="k"
          :class="cn('gridItem')"
        />
      </div>
    </div>

    <div :class="cn('inner')">
      <div :class="cn('menu')"></div>

      <Swiper
        ref="carousel"
        @swiper="onSwiper"
        v-bind="{ ...swiperOptions }"
        :auto-update="false"
        :delete-instance-on-destroy="false"
        :cleanup-styles-on-destroy="false"
        :class="cn('swiper')"
      >
        <SwiperSlide :class="cn('menuSlide')">
          <div :class="cn('menu')"></div>
        </SwiperSlide>

        <SwiperSlide
          v-for="(product, k) in products"
          :key="product.id || `home_product_${k}`"
          :class="cn('slide')"
        >
          <HomeSectionProductsCard :index="k" :product="product" />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { bem } from '../../utils/classnames'
import { SwiperOptions } from 'swiper/types'
import { Swiper, SwiperSlide } from 'swiper/vue'
import HomeSectionProductsCard from './HomeSectionProductsCard.vue'

const cn = bem('home-section-products')

defineProps<{
  products: any[]
}>()

const swiperOptions = {
  slidesPerView: 'auto' as const,
  allowTouchMove: true,
  simulateTouch: true,
  grabCursor: true,
  mousewheel: true,
} as SwiperOptions

const swiperRef = ref(null)
const onSwiper = (swiper: any) => {
  swiperRef.value = swiper
}
</script>

<style lang="scss">
@import '~styles/base.scss';

.home-section-products {
  padding-top: 80px;
  padding-bottom: 80px;
  width: 100%;
}

.home-section-products__gridWrap {
  @include wrapper;
  padding-left: columns(3, 0);
  padding-right: columns(3, 0);

  @include breakpoint('l') {
    display: none;
  }

  @include breakpoint('xl+') {
    margin-left: 25px;
    margin-right: 25px;
  }
}

.home-section-products__grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 80px 100px;

  @media screen and (min-width: 800px) and (max-width: 919px) {
    gap: 80px 20px;
  }
}

.home-section-products__gridItem {
  width: 100%;
}

.home-section-products__inner {
  @include breakpoint('l') {
    @include wrapper;
    flex-direction: column;
    display: flex;
    gap: 40px;
  }

  @include breakpoint('l+') {
    display: none;
  }
}

.home-section-products__menu {
  position: relative;
  z-index: 5;
  min-height: 1px;

  @include breakpoint('l+') {
    display: none;
  }
}

.home-section-products__menuSlide {
  @include breakpoint('l+') {
    width: calc(var(--inset) + columns(9, 0));
  }

  @include breakpoint('l') {
    display: none;
  }
}

.home-section-products__swiper {
  width: 100%;
  overflow: hidden;

  @include breakpoint('l+') {
    display: none;
  }
}

.home-section-products .swiper-wrapper {
  width: fit-content;

  @include breakpoint('l') {
    width: 100%;
  }
}

.home-section-products .swiper-slide {
  @include breakpoint('l+') {
    &:not(:first-child) {
      width: columns(4, -1);
    }

    &:last-child {
      margin-right: var(--inset);
    }

    &:not(:first-child):not(:last-child) {
      margin-right: columns(2, 1);
    }
  }

  @include breakpoint('l') {
    &:not(.home-section-products__menuSlide) {
      width: columns(4, -0.5);
    }

    &:not(:last-child) {
      margin-right: var(--gutter);
      padding-right: 2px;
    }
  }
}
</style>
